import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoBeerOutline } from "react-icons/io5";
import { IoSunnyOutline, IoMoonOutline } from "react-icons/io5";
import { useSettings } from '../../hooks/useSettings';
import { useTheme } from '../../context/ThemeContext';
import { useOrganization, useSwitchRestaurant } from '../../hooks/useOrganization';
import { useCurrentAdmin } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import ldmLogo from '../../assets/ldm-logo.png';

const DASHBOARD_PATHS = ['/dashboard', '/account', '/org-dashboard'];

const Header = ({ menuState, setMenuState, onStartTour }) => {
  const { data: settings } = useSettings();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const currentAdmin = useCurrentAdmin();
  const { data: orgData } = useOrganization({ enabled: !!currentAdmin });
  const switchRestaurant = useSwitchRestaurant();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);
  const restaurantName = settings?.restaurantName || '';
  const isDashboard = location.pathname === '/dashboard';
  const isAppPage = currentAdmin && DASHBOARD_PATHS.includes(location.pathname);

  const hasMultipleLocations = orgData?.restaurants?.length > 1;

  // Close switcher on outside click or Escape
  useEffect(() => {
    if (!switcherOpen) return;
    const handleClick = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [switcherOpen]);

  const handleSwitch = (restaurantId) => {
    setSwitcherOpen(false);
    switchRestaurant.mutate(restaurantId, {
      onSuccess: () => {
        navigate('/dashboard');
        window.location.reload();
      },
    });
  };

  return (
    <div className="site-header">
      <header>
        {/* Brand */}
        <div className="brand-icon">
          {isAppPage ? (
            <Link to="/dashboard">
              <span className="icon">
                <IoBeerOutline />
              </span>
              {restaurantName}
            </Link>
          ) : (
            <Link to="/" className="brand-logo-link">
              <img src={ldmLogo} alt="Live Digital Menus" className="brand-logo" />
            </Link>
          )}
          {isAppPage && hasMultipleLocations && (
            <div className="restaurant-switcher" ref={switcherRef}>
              <button
                className="switcher-trigger"
                onClick={() => setSwitcherOpen(!switcherOpen)}
                aria-label="Switch restaurant location"
                aria-expanded={switcherOpen}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {switcherOpen && (
                <div className="switcher-dropdown" role="menu">
                  <div className="switcher-label">Switch Location</div>
                  {orgData.restaurants.map((r) => (
                    <button
                      key={r.id}
                      className={`switcher-option${r.id === currentAdmin?.restaurantId ? ' switcher-option--active' : ''}`}
                      role="menuitem"
                      onClick={() => r.id !== currentAdmin?.restaurantId && handleSwitch(r.id)}
                      disabled={r.id === currentAdmin?.restaurantId}
                      aria-label={`Switch to ${r.name}${r.id === currentAdmin?.restaurantId ? ' (current)' : ''}`}
                    >
                      <span className="switcher-option-name">{r.name}</span>
                      <span className="switcher-option-meta">
                        {r.menuCount + r.beerCount} items
                      </span>
                    </button>
                  ))}
                  <Link
                    to="/org-dashboard"
                    className="switcher-overview-link"
                    onClick={() => setSwitcherOpen(false)}
                  >
                    View All Locations
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="header-controls">
          {isDashboard && (
            <button
              onClick={() => onStartTour?.()}
              className="tour-trigger"
              aria-label="Show guided tour"
              title="Guided tour"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
          )}
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="theme-toggle"
            aria-label="Toggle theme"
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <IoSunnyOutline /> : <IoMoonOutline />}
          </button>

          {/* Menu Button */}
          <button
            onClick={() => setMenuState(!menuState)}
            className={`menu-trigger ${menuState ? 'menu-close' : ''}`}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        <Sidebar show={menuState} setMenuState={setMenuState} />
      </header>
    </div>
  );
};

export default Header;
